/**
 * Cleans an analysis config to what a tier may use. Pure, so the server and both apps can run
 * the same rules and reach the same result.
 */

import { z } from 'zod';
import type { AnalysisConfig } from './analysisConfig';
import {
  canUseFactor,
  countableFactorTotal,
  countEnabledFactors,
  countsTowardLimit,
  FACTOR_REGISTRY,
  type FactorDataSources,
  type FactorDefinition,
  getFactor,
  isFactorAvailable,
  isFactorEnabled,
} from './factorRegistry';
import {
  FACTOR_TOGGLE_LIMITS,
  GATED_FEATURES,
  type GatedFeature,
  getRequiredTier,
  hasAccess,
  isFeatureHidden,
} from './featureAccess';
import {
  type NotificationPreferences,
  type NotificationSubGroup,
  subGroupFeature,
} from './notification';
import {
  DEFAULT_MAX_LEG_COUNT,
  DEFAULT_MIN_LEG_COUNT,
  DEFAULT_TARGET_ODDS_MAX,
  DEFAULT_TARGET_ODDS_MIN,
} from './strategyConstants';
import { type SubscriptionTier, TIER_DISPLAY_NAMES } from './subscriptionTier';
import { getIncludedTiers, TIER_CRITERIA } from './tierCriteria';

export type ConfigAdjustment =
  | { kind: 'factor-off'; id: string; reason: 'limit' | 'tier' | 'unavailable' }
  | { kind: 'history-source'; from: 'personal' | 'community' }
  | { kind: 'lottery-tier'; maxLegCount: number }
  /** Settings the tier cannot see; never named to the user. */
  | { kind: 'hidden-settings' };

/** API error code when an explicit request goes past the tier, so the apps can show the reason. */
export const TIER_LIMIT_ERROR_CODE = 'TIER_LIMIT';

const ConfigAdjustmentSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('factor-off'),
    id: z.string(),
    reason: z.enum(['limit', 'tier', 'unavailable']),
  }),
  z.object({ kind: z.literal('history-source'), from: z.enum(['personal', 'community']) }),
  z.object({ kind: z.literal('lottery-tier'), maxLegCount: z.number() }),
  z.object({ kind: z.literal('hidden-settings') }),
]);

/** The `adjusted` list a response carries; entries the app does not recognise are dropped. */
export function adjustmentsFromBody(body: unknown): ConfigAdjustment[] {
  if (typeof body !== 'object' || body === null || !('adjusted' in body)) return [];
  if (!Array.isArray(body.adjusted)) return [];
  return body.adjusted.flatMap((entry: unknown) => {
    const parsed = ConfigAdjustmentSchema.safeParse(entry);
    return parsed.success ? [parsed.data] : [];
  });
}

/** The upgradeFeature a TIER_LIMIT response carries, if it is a real feature key. */
export function upgradeFeatureFromBody(body: unknown): GatedFeature | null {
  if (typeof body !== 'object' || body === null || !('upgradeFeature' in body)) return null;
  const feature = body.upgradeFeature;
  return GATED_FEATURES.find((f) => f === feature) ?? null;
}

export interface TierPolicyContext {
  tier: SubscriptionTier;
  /** Factor ids, least predictive first; ids not listed are trimmed after these. */
  ranking?: readonly string[];
  /** Optional data sources and whether each is on. */
  sources?: FactorDataSources;
}

export interface TierPolicyResult {
  config: AnalysisConfig;
  adjusted: ConfigAdjustment[];
}

/** Config namespaces that belong to one gated feature as a whole, so unknown keys fail closed. */
const GATED_NAMESPACES: ReadonlyArray<readonly ['ml' | 'parlayIntelligence', GatedFeature]> = [
  ['ml', 'ml_generation_mode'],
  ['parlayIntelligence', 'ml_parlay_optimizer'],
];

const HISTORY_SOURCE_FEATURES = {
  personal: 'history_personal',
  community: 'history_community',
} as const satisfies Record<'personal' | 'community', GatedFeature>;

/** The most factors a tier may have on: its limit, never more than every countable factor. */
export function factorLimitFor(tier: SubscriptionTier): number {
  return Math.min(FACTOR_TOGGLE_LIMITS[tier], countableFactorTotal());
}

function setFactor(config: AnalysisConfig, def: FactorDefinition, on: boolean): AnalysisConfig {
  const section = {
    ...((config[def.section] as Record<string, unknown> | undefined) ?? {}),
    [def.key]: on,
  };
  return { ...config, [def.section]: section };
}

/** Order factors are switched off in when over the limit: ranked first, then the rest from the end. */
function trimOrder(ranking: readonly string[]): FactorDefinition[] {
  const rankedIds = new Set(ranking);
  const ranked = ranking
    .map((id) => FACTOR_REGISTRY.find((f) => f.id === id))
    .filter((f): f is FactorDefinition => f !== undefined);
  const unranked = FACTOR_REGISTRY.filter((f) => !rankedIds.has(f.id)).reverse();
  return [...ranked, ...unranked].filter(countsTowardLimit);
}

/** Whether a leg and odds range reaches the Lottery Ticket tier (unset values take the defaults). */
export function reachesLotteryTier(strategy: AnalysisConfig['parlayStrategy'] = {}): boolean {
  return getIncludedTiers(
    strategy.minLegCount ?? DEFAULT_MIN_LEG_COUNT,
    strategy.maxLegCount ?? DEFAULT_MAX_LEG_COUNT,
    strategy.targetOddsMin ?? DEFAULT_TARGET_ODDS_MIN,
    strategy.targetOddsMax ?? DEFAULT_TARGET_ODDS_MAX,
  ).some((t) => t.key === 'lottery');
}

/** What both apps show when the chosen range reaches Lottery Ticket on a plan without it. */
export function lotteryWarning(): string {
  const lottery = TIER_CRITERIA.find((t) => t.key === 'lottery');
  const plan = TIER_DISPLAY_NAMES[getRequiredTier('lottery_ticket_tier')];
  return `${lottery?.minLegs}+ legs at +${lottery?.minOdds} or longer is a Lottery Ticket parlay, which needs the ${plan} plan. Lower max legs to ${maxNonLotteryLegs()}, or keep max odds under +${lottery?.minOdds}, to save.`;
}

/** Highest leg count a parlay can have without reaching the Lottery Ticket tier. */
function maxNonLotteryLegs(): number {
  return Math.max(...TIER_CRITERIA.filter((t) => t.key !== 'lottery').map((t) => t.maxLegs));
}

export function enforceAnalysisConfigForTier(
  input: AnalysisConfig,
  { tier, ranking = [], sources = {} }: TierPolicyContext,
): TierPolicyResult {
  let config: AnalysisConfig = { ...input };
  const adjusted: ConfigAdjustment[] = [];
  let droppedHidden = false;

  for (const [namespace, feature] of GATED_NAMESPACES) {
    if (config[namespace] !== undefined && !hasAccess(tier, feature)) {
      const { [namespace]: _dropped, ...rest } = config;
      config = rest;
      droppedHidden = true;
    }
  }

  for (const def of FACTOR_REGISTRY) {
    if (!isFactorEnabled(config, def.id)) continue;
    if (!canUseFactor(tier, def)) {
      config = setFactor(config, def, false);
      if (def.feature && isFeatureHidden(tier, def.feature)) droppedHidden = true;
      else adjusted.push({ kind: 'factor-off', id: def.id, reason: 'tier' });
    } else if (!isFactorAvailable(def, sources)) {
      config = setFactor(config, def, false);
      adjusted.push({ kind: 'factor-off', id: def.id, reason: 'unavailable' });
    }
  }

  const historySource = config.otherFactors?.historySource;
  if (
    (historySource === 'personal' || historySource === 'community') &&
    !hasAccess(tier, HISTORY_SOURCE_FEATURES[historySource])
  ) {
    config = { ...config, otherFactors: { ...config.otherFactors, historySource: 'off' } };
    adjusted.push({ kind: 'history-source', from: historySource });
  }

  if (!hasAccess(tier, 'lottery_ticket_tier')) {
    const strategy = config.parlayStrategy ?? {};
    if (reachesLotteryTier(strategy)) {
      const cap = maxNonLotteryLegs();
      config = {
        ...config,
        parlayStrategy: {
          ...strategy,
          maxLegCount: cap,
          minLegCount: Math.min(strategy.minLegCount ?? DEFAULT_MIN_LEG_COUNT, cap),
        },
      };
      adjusted.push({ kind: 'lottery-tier', maxLegCount: cap });
    }
  }

  const limit = factorLimitFor(tier);
  if (countEnabledFactors(config) > limit) {
    for (const def of trimOrder(ranking)) {
      if (countEnabledFactors(config) <= limit) break;
      if (!isFactorEnabled(config, def.id)) continue;
      config = setFactor(config, def, false);
      adjusted.push({ kind: 'factor-off', id: def.id, reason: 'limit' });
    }
  }

  if (droppedHidden) adjusted.push({ kind: 'hidden-settings' });
  return { config, adjusted };
}

/** Drops notification rows whose feature the tier lacks, so they fall back to their defaults. */
export function enforceNotificationPreferencesForTier(
  prefs: Partial<NotificationPreferences>,
  tier: SubscriptionTier,
): Partial<NotificationPreferences> {
  const kept: Partial<NotificationPreferences> = {};
  for (const [key, row] of Object.entries(prefs) as [
    NotificationSubGroup,
    NotificationPreferences[NotificationSubGroup],
  ][]) {
    const feature = subGroupFeature(key);
    if (!feature || hasAccess(tier, feature)) kept[key] = row;
  }
  return kept;
}

/** Factor names for a message: up to three, then how many more. */
function nameList(ids: readonly string[]): string {
  const names = ids.map((id) => getFactor(id)?.label ?? id);
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`;
}

/** One plain message for what the policy changed, or null when nothing did. */
export function describeConfigAdjustments(
  adjusted: readonly ConfigAdjustment[],
  tier: SubscriptionTier,
): string | null {
  const off = (reason: 'limit' | 'tier' | 'unavailable') =>
    adjusted.flatMap((a) => (a.kind === 'factor-off' && a.reason === reason ? [a.id] : []));
  const parts: string[] = [];
  const overLimit = off('limit');
  if (overLimit.length > 0) {
    parts.push(
      `Switched off to fit your plan's limit of ${factorLimitFor(tier)} factors: ${nameList(overLimit)}.`,
    );
  }
  const notInPlan = off('tier');
  if (notInPlan.length > 0) parts.push(`Not included in your plan: ${nameList(notInPlan)}.`);
  const unavailable = off('unavailable');
  if (unavailable.length > 0) {
    parts.push(`Data source unavailable right now: ${nameList(unavailable)}.`);
  }
  if (adjusted.some((a) => a.kind === 'history-source')) {
    parts.push('History source set to Off: that source is not included in your plan.');
  }
  const lottery = adjusted.find((a) => a.kind === 'lottery-tier');
  if (lottery?.kind === 'lottery-tier') {
    parts.push(
      `Lottery Ticket parlays are not included in your plan, so max legs is ${lottery.maxLegCount}.`,
    );
  }
  if (adjusted.some((a) => a.kind === 'hidden-settings')) {
    parts.push('Some settings are not part of your plan and were removed.');
  }
  return parts.length > 0 ? parts.join(' ') : null;
}

/**
 * What the policy would change only because of a request: run on the stored config and on the
 * config with the request applied, and keep what is new. An explicit request that goes past the
 * tier is refused rather than trimmed (owner decision, 2026-10-01).
 */
export function findRequestViolations(
  stored: AnalysisConfig,
  requested: AnalysisConfig,
  context: TierPolicyContext,
): ConfigAdjustment[] {
  const before = new Set(
    enforceAnalysisConfigForTier(stored, context).adjusted.map((a) => JSON.stringify(a)),
  );
  return enforceAnalysisConfigForTier(requested, context).adjusted.filter(
    (a) => !before.has(JSON.stringify(a)),
  );
}

/**
 * The plan feature a refusal can be fixed by upgrading for: the first refused item whose feature is
 * Locked (never Hidden) for this tier. Null when no upgrade fixes it, such as the factor limit.
 */
export function upgradeFeatureFor(
  violations: readonly ConfigAdjustment[],
  tier: SubscriptionTier,
): GatedFeature | null {
  for (const a of violations) {
    let feature: GatedFeature | undefined;
    if (a.kind === 'factor-off' && a.reason === 'tier') feature = getFactor(a.id)?.feature;
    else if (a.kind === 'history-source') feature = HISTORY_SOURCE_FEATURES[a.from];
    else if (a.kind === 'lottery-tier') feature = 'lottery_ticket_tier';
    if (feature && !isFeatureHidden(tier, feature)) return feature;
  }
  return null;
}

/** Why a request was refused, naming the plan for Locked items and never naming Hidden ones. */
export function describeRequestViolations(
  violations: readonly ConfigAdjustment[],
  requested: AnalysisConfig,
  tier: SubscriptionTier,
): string {
  const parts: string[] = [];
  if (violations.some((a) => a.kind === 'factor-off' && a.reason === 'limit')) {
    parts.push(
      `That would turn on ${countEnabledFactors(requested)} factors; your plan allows ${factorLimitFor(tier)}. Turn some off first.`,
    );
  }
  for (const a of violations) {
    if (a.kind === 'factor-off' && a.reason === 'tier') {
      const def = getFactor(a.id);
      if (def?.feature) {
        parts.push(
          `${def.label} needs the ${TIER_DISPLAY_NAMES[getRequiredTier(def.feature)]} plan.`,
        );
      }
    } else if (a.kind === 'factor-off' && a.reason === 'unavailable') {
      parts.push(
        `${getFactor(a.id)?.label ?? a.id} is unavailable right now: its data source is off.`,
      );
    } else if (a.kind === 'history-source') {
      parts.push(
        `${a.from === 'personal' ? 'Personal' : 'Community'} history needs the ${TIER_DISPLAY_NAMES[getRequiredTier(HISTORY_SOURCE_FEATURES[a.from])]} plan.`,
      );
    } else if (a.kind === 'lottery-tier') {
      parts.push(
        `Lottery Ticket parlays need the ${TIER_DISPLAY_NAMES[getRequiredTier('lottery_ticket_tier')]} plan.`,
      );
    } else if (a.kind === 'hidden-settings') {
      parts.push('That setting is not available.');
    }
  }
  return parts.join(' ');
}
